import React, { useEffect, useState } from "react";
import "./Transaction.scss";
import {
  Card,
  Checkbox,
  Col,
  Empty,
  Input,
  Row,
  Segmented,
  Select,
  Skeleton,
  Statistic,
  Tabs,
} from "antd";
import TransactionItem from "@components/TransactionItem/TransactionItem";
import { ProductApi } from "services/ProductApi";
import { errorLog } from "logging";
import Helpers from "@utils/Helpers";
import LazyLoading from "@components/LazyLoading/LazyLoading";
import cart from "@assets/icons/shopping-cart.svg";
import dollar from "@assets/icons/dollar.svg";
import { SearchOutlined } from "@ant-design/icons";
import { useUser } from "contexts/UserContext";
import { Link } from "react-router-dom";

const Transaction: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState("1");
  const [isFetch, setIsFetch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { userProfile } = useUser();
  const [selectRole, setSelectRole] = useState("consumer");
  const [orderList, setOrderList] = useState([]);
  const [producerOrderList, setProducerOrderList] = useState([]);
  const urlParams = new URLSearchParams(window.location.search);
  const target = urlParams.get("target");
  const idToScroll = urlParams.get("id");
  const [orderQuantity, setOrderQuantity] = useState({
    all: 0,
    orderPlace: 0,
    confirm: 0,
    processing: 0,
    shipping: 0,
    complete: 0,
    cancel: 0,
    return: 0,
  });

  const menuBtn = [
    { key: "1", label: `All (${orderQuantity.all})`, children: "" },
    {
      key: "2",
      label: `Order Placed (${orderQuantity.orderPlace})`,
      children: "",
    },
    {
      key: "3",
      label: `Order Confirmation (${orderQuantity.confirm})`,
      children: "",
    },
    {
      key: "4",
      label: `Order Processing (${orderQuantity.processing})`,
      children: "",
    },
    {
      key: "5",
      label: `Shipping & Delivery (${orderQuantity.shipping})`,
      children: "",
    },
    {
      key: "6",
      label: `Delivery Completion (${orderQuantity.complete})`,
      children: "",
    },
    { key: "7", label: `Cancellation (${orderQuantity.cancel})`, children: "" },
    {
      key: "8",
      label: `Return/Refund (${orderQuantity.return})`,
      children: "",
    },
  ];

  // HANDLE CALCULATE ORDER QUANTITY
  useEffect(() => {
    const isConsumer = selectRole === "consumer";
    const currentList = isConsumer ? orderList : producerOrderList;
    const countByState = currentList.reduce((acc: any, item: any) => {
      const stage = item?.transaction_status?.stage;
      if (stage !== undefined && stage !== null) {
        acc[stage] = (acc[stage] || 0) + 1;
      }
      return acc;
    }, {});

    setOrderQuantity({
      all: currentList.length,
      orderPlace: isConsumer ? countByState["2"] || 0 : 0,
      confirm: countByState["2"] || 0,
      processing: countByState["3"] || 0,
      shipping: countByState["4"] || 0,
      complete: countByState["5"] || 0,
      cancel: 0,
      return: 0,
    });
  }, [orderList, producerOrderList, selectRole]);

  // HANDLE SCROLL TO TARGET ORDER
  useEffect(() => {
    if (target && idToScroll) {
      setSelectRole(target === "producer" ? "producer" : "consumer");
      setTimeout(() => {
        const element = document.getElementById(idToScroll.toString());
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 1000);
    }
  }, [target, idToScroll]);

  // FETCH ORDER
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await ProductApi.GetOrder();
        if (response && response.status === 200) {
          setOrderList(response.data.data);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch orders");
        errorLog("Transaction.tsx - fetchOrder", error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchOrderProducer = async () => {
      try {
        const response = await ProductApi.GetProducerOrder();
        if (response.status === 200) {
          setProducerOrderList(response.data.data);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch orders");
        errorLog("Transaction.tsx - fetchOrderProducer", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isFetch) {
      fetchOrder();
      fetchOrderProducer();
      setIsLoading(true);
      setIsFetch(true);
    }
  }, [isFetch]);

  return (
    <div className="Transaction">
      <div className="container">
        <div className="title">
          Transactions
          <div className="transaction">
            <Segmented
              className={`role-select ${
                userProfile?.type_ids?.includes(2) ? "producer" : "consumer"
              }`}
              size={"middle"}
              options={[
                {
                  label: (
                    <div>
                      <img src={cart} alt="cart" width={20} /> Consumers
                    </div>
                  ),
                  value: "consumer",
                },
                {
                  label: (
                    <div>
                      <img src={dollar} alt="cart" width={24} /> Producers
                    </div>
                  ),
                  disabled: !userProfile?.type_ids?.includes(2),
                  value: "producer",
                },
              ]}
              value={selectRole}
              onChange={(value) => setSelectRole(value)}
            />
          </div>
        </div>
        {selectRole === "consumer" ? (
          <div className="buyer-transaction">
            <div className="transaction-menu consumer">
              <Tabs
                className="tabs"
                tabPosition="top"
                style={{ width: "100%" }}
                activeKey={activeSubTab}
                onChange={(key) => {
                  setActiveSubTab(key);
                }}
                items={menuBtn}
              />
            </div>
            <div className="search-group">
              <span>All ({orderList.length})</span>
              <Input
                size="middle"
                variant="filled"
                prefix={<SearchOutlined />}
                style={{
                  color: "#808080",
                  width: "50%",
                }}
                placeholder="Search"
              />
            </div>
            <div className="transaction-list">
              {!isLoading ? (
                orderList.length > 0 ? (
                  orderList.map((order: any, index: number) => {
                    if (activeSubTab === "1") {
                      return (
                        <LazyLoading key={index}>
                          <TransactionItem
                            key={index}
                            data={order}
                            type="consumer"
                          />
                        </LazyLoading>
                      );
                    } else {
                      if (
                        order.transaction_status.stage ===
                        Number(activeSubTab) - 1
                      ) {
                        return (
                          <LazyLoading key={index}>
                            <TransactionItem
                              key={index}
                              data={order}
                              type="consumer"
                            />
                          </LazyLoading>
                        );
                      }
                    }
                  })
                ) : (
                  <div className="empty-wrap">
                    <Empty />
                  </div>
                )
              ) : (
                Array.from({ length: 5 }).map((item, index) => {
                  return (
                    <div className="transaction-skeleton" key={index}>
                      <Skeleton active />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <div className="seller-transaction">
            <Row gutter={8} style={{ marginTop: 24 }}>
              <Col flex="auto">
                <Card>
                  <Statistic
                    title={
                      <span style={{ color: "#000" }}>Awaiting Carrier</span>
                    }
                    value={0}
                    valueStyle={{ color: "var(--primary-500)", fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col flex="auto">
                <Card>
                  <Statistic
                    title={
                      <span style={{ color: "#000" }}>Shipping Overdue</span>
                    }
                    value={0}
                    valueStyle={{ color: "var(--primary-500)", fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col flex="auto">
                <Card>
                  <Statistic
                    title={
                      <span style={{ color: "#000" }}>
                        Cancellation Requested
                      </span>
                    }
                    value={0}
                    valueStyle={{ color: "var(--primary-500)", fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col flex="auto">
                <Card>
                  <Statistic
                    title={
                      <span style={{ color: "#000" }}>
                        Return/Refund Requested
                      </span>
                    }
                    value={0}
                    valueStyle={{ color: "var(--primary-500)", fontSize: 20 }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title={<span style={{ color: "#000" }}>Disputes</span>}
                    value={0}
                    valueStyle={{ color: "var(--primary-500)", fontSize: 20 }}
                  />
                </Card>
              </Col>
            </Row>
            <div className="transaction-menu producer">
              <Tabs
                className="tabs"
                tabPosition="top"
                style={{ width: "100%" }}
                activeKey={activeSubTab}
                onChange={(key) => {
                  setActiveSubTab(key);
                }}
                items={menuBtn}
              />
            </div>
            <div className="search-filter-group">
              <div className="search-filter-item">
                <Input
                  size="middle"
                  variant="filled"
                  prefix={<SearchOutlined />}
                  style={{
                    color: "#808080",
                    width: "40%",
                  }}
                  placeholder="Search"
                />
                <Select
                  defaultValue="All status"
                  variant="filled"
                  style={{ width: "20%" }}
                >
                  <Select.Option value="1">All status</Select.Option>
                  <Select.Option value="2">Processing</Select.Option>
                  <Select.Option value="3">Confirmed</Select.Option>
                  <Select.Option value="4">Ready to Pickup</Select.Option>
                  <Select.Option value="5">Picked</Select.Option>
                  <Select.Option value="6">Shipping</Select.Option>
                  <Select.Option value="7">Arrived</Select.Option>
                  <Select.Option value="8">Conpleted</Select.Option>
                </Select>
                <Select
                  defaultValue="30 days"
                  variant="filled"
                  style={{ width: "20%" }}
                >
                  <Select.Option value="1">30 days</Select.Option>
                  <Select.Option value="2">Today</Select.Option>
                  <Select.Option value="3">7 days</Select.Option>
                  <Select.Option value="4">90 days</Select.Option>
                </Select>
                <Select
                  defaultValue="Date: Newest"
                  variant="filled"
                  style={{ width: "20%" }}
                >
                  <Select.Option value="1">Date: Newest</Select.Option>
                  <Select.Option value="2">Date: Oldest</Select.Option>
                  <Select.Option value="3">Highest Price</Select.Option>
                  <Select.Option value="4">Lowest Price</Select.Option>
                </Select>
              </div>
              <div className="select-all">
                <Checkbox>All ({producerOrderList.length})</Checkbox>
                <Link to={`#`} className="download-btn">
                  Download Report
                </Link>
              </div>
            </div>
            <div className="transaction-list">
              {isFetch ? (
                producerOrderList.length > 0 ? (
                  producerOrderList.map((order: any, index: number) => {
                    if (activeSubTab === "1") {
                      return (
                        <LazyLoading key={index}>
                          <TransactionItem
                            key={index}
                            data={order}
                            type="producer"
                          />
                        </LazyLoading>
                      );
                    } else {
                      if (
                        order.transaction_status.stage ===
                        Number(activeSubTab) - 1
                      ) {
                        return (
                          <LazyLoading key={index}>
                            <TransactionItem
                              key={index}
                              data={order}
                              type="producer"
                            />
                          </LazyLoading>
                        );
                      }
                    }
                  })
                ) : (
                  <div className="empty-wrap">
                    <Empty />
                  </div>
                )
              ) : (
                Array.from({ length: 5 }).map((item, index) => {
                  return (
                    <div className="transaction-skeleton" key={index}>
                      <Skeleton active />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transaction;
